import {Trans} from '@lingui/macro';
import React, {FC, useCallback, useContext, useState} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {useDebounce, useLastCallEffect, useToggle} from '../../../../../srcCommon/helpers/hookHelpers';
import internalDiscussion from '../../../../assets/images/internalDiscussion.svg';
import {CommentModel} from '../../../../models/api/commentModel';
import {InboxModel} from '../../../../models/api/inboxModel';
import {EventEntryPointsEnum, InterfaceEventsEnum, trackInterfaceEvent} from '../../../../services/tracking';
import {FindOnlineStatus} from '../../../../store/data/teammates/onlineTeammatesSelectors';
import {GrammarlyContext} from '../../../grammarly/grammarlyContext';
import {
  CommentComposer,
  CommentComposerPublicProps,
  SendCommentOptions,
} from '../commentComposer/commentComposer';
import {InternalRecipientsEditor, InternalRecipientsEditorProps} from './internalRecipientsEditor';
import {InternalTopicEditor, InternalTopicEditorProps} from './internalTopicEditor';

/*
 * Props.
 */

interface InternalComposerProps
  extends Omit<CommentComposerPublicProps, 'attachments' | 'isExpanded' | 'onExpandedToggle'>,
    Omit<InternalRecipientsEditorProps, 'inbox' | 'onInboxChange'>,
    Omit<InternalTopicEditorProps, 'onTopicChange' | 'onTopicBlur'> {
  comment: CommentModel;
  onTopicChange: HandlerOf<string>;
  findOnlineStatus: FindOnlineStatus;
  isComposerPopout: boolean;
}

/*
 * Constants.
 */

const topicChangeDebounceInterval = 3000;

/*
 * Style.
 */

const StyledComposerDiv = styled.div`
  min-width: 540px;

  display: flex;
  flex-direction: column;

  background-image: url(${internalDiscussion});
  background-repeat: no-repeat;
  background-position: center 140px;
  background-color: ${(p) => p.theme.greys.shade20};
  padding-left: 10px;
  padding-right: 2px;
`;

const StyledScrollableDiv = styled.div`
  flex: initial;
  /** Don't shrink smaller than one line of recipients + discussion topic. */
  min-height: 92px;

  display: flex;
  flex-direction: column;
  overflow: auto;
  margin-left: -10px;
`;

const StyledComposerWrapperDiv = styled.div`
  flex: auto;

  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow: hidden;
`;

const StyledCommentComposer = styled(CommentComposer)`
  /** Make the discussion topic and comment composer more cozy. */
  margin-top: -16px;
`;

/*
 * Component.
 */

export const InternalComposer: FC<InternalComposerProps> = (props) => {
  const [inbox, setInbox] = useState<InboxModel | undefined>();

  const onTopicChange = useDebounce(props.onTopicChange, topicChangeDebounceInterval);
  const onTopicBlur = () => onTopicChange.flush();

  const onCommentSend = (options: SendCommentOptions) => {
    onTopicChange.flush();
    return props.sendComment({
      ...options,
      inboxId: inbox ? inbox.id : undefined,
    });
  };

  // A teammate or inbox recipient is required to send a new discussion.
  const isSendDisabled = props.teammates.length === 0 && !inbox;
  const [isExpanded, toggleExpanded] = useToggle(true);

  const trackExpandedToggle = useLastCallEffect((entryPoint: EventEntryPointsEnum) => {
    trackInterfaceEvent(InterfaceEventsEnum.COMMENT_COMPOSER_EXPAND_ICON_CLICK, {
      action: isExpanded ? 'expand' : 'collapse',
      entry_point: entryPoint,
    });
  });

  const onExpandedToggle: HandlerOf<EventEntryPointsEnum> = useCallback(
    (entryPoint) => {
      toggleExpanded();
      trackExpandedToggle(entryPoint);
    },
    [toggleExpanded, trackExpandedToggle],
  );

  const {setComposerHeight} = useContext(GrammarlyContext);

  return (
    <StyledComposerDiv>
      <StyledScrollableDiv>
        <InternalRecipientsEditor
          inbox={inbox}
          onInboxChange={setInbox}
          teammates={props.teammates}
          onTeammatesChange={props.onTeammatesChange}
          findOnlineStatus={props.findOnlineStatus}
        />
        <InternalTopicEditor
          comment={props.comment}
          onTopicChange={onTopicChange}
          onTopicBlur={onTopicBlur}
        />
      </StyledScrollableDiv>
      <StyledComposerWrapperDiv>
        <StyledCommentComposer
          {...props}
          attachments={props.comment.attachments}
          sendComment={onCommentSend}
          isSendDisabled={isSendDisabled}
          isExpanded={isExpanded}
          onExpandedToggle={onExpandedToggle}
          placeholder={<Trans id="internalcomposer-commentcomposer-placeholder">Start a discussion</Trans>}
          sendLabel={<Trans id="internalcomposer-commentcomposer-sendlabel">Send</Trans>}
          shouldHideCommentComposerInfoBubble
          hasContentHeight
          setComposerHeight={props.isComposerPopout ? setComposerHeight : undefined}
        />
      </StyledComposerWrapperDiv>
    </StyledComposerDiv>
  );
};
